<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
	<div class="flex items-center justify-center h-screen">
		<div class="box">
			<img src="/images/login-logo.png" class="logo block mx-auto" alt="Logo">
			<Transition name="errormessage">
				<div v-show="errorMessage">
					<Message class="ml-8 mr-8 mb-8" severity="error">
						<template #icon>
							<GlyphIcon :icon="GlyphIconName['triangle-empty-alert']" size="32" color="var(--p-message-error-color)" />
						</template>
						{{  errorMessage }}
					</Message>
				</div>
			</Transition>
			<Transition name="errormessage">
				<div v-show="successMessage">
					<Message class="ml-8 mr-8 mb-8" severity="success">
						<template #icon>
							<GlyphIcon :icon="GlyphIconName['circle-empty-check']" size="32" color="var(--p-message-success-color)" />
						</template>
						{{ successMessage }}
					</Message>
				</div>
			</Transition>
			<form @submit.prevent="login">
				<Transition name="section">
					<div class="pl-8 pr-8" v-show="mode===LoginDialogMode.USERNAME_PASSWORD">

						<FloatLabel variant="on" class="mb-4">
							<InputText v-model="auth.username" type="email" id="email" autocomplete="username webauthn" @keyup.enter="login" :disabled="isWorking" fluid />
							<label for="email">{{  t('login.email') }}</label>
						</FloatLabel>

						<FloatLabel variant="on">
							<InputPassword v-model="auth.password" id="password" @keyup.enter="login" :disabled="isWorking" fluid />
							<label for="password">{{  t('login.password') }}</label>
						</FloatLabel>

						<div class="text-right">
							<Button v-if="passkeyAvailable && passkeyCodeOffered" type="button" variant="link" @click="showPasskeyEnroll()" size="small">{{ t('login.passkeyCodeButton') }}</Button>
							<Button type="button" variant="link" @click="showReset()" size="small">{{ t('login.resetPassword') }}</Button>
						</div>

						<div class="flex justify-center items-center space-x-2 mt-2">
							<ToggleSwitch v-model="auth.persist" :disabled="isWorking" />
							<label @click="auth.persist=!auth.persist">{{ t('login.remember') }}</label>
						</div>

						<div class="text-sm text-muted-color mt-3" v-if="passkeyUnavailable">{{ t('login.passkeyUnavailable') }}</div>
					</div>
				</Transition>

				<Transition name="section">
					<div class="flex flex-col items-center" v-show="mode===LoginDialogMode.SMS_ENTRY">
						<label for="smsKey">{{ t('login.smsCode') }}</label>
						<InputOtp v-model="auth.smsKey" :length="4" size="large" id="smsKey" @keyup.enter="login" :disabled="isWorking"
						:pt="{ input: { 'autocomplete': 'section-login one-time-code' } }" />
					</div>
				</Transition>

				<Transition name="section">
					<div class="flex flex-col items-center" v-show="mode===LoginDialogMode.AUTHENTICATOR_ENTRY || mode===LoginDialogMode.AUTHENTICATOR_SETUP">
						<label v-show="mode===LoginDialogMode.AUTHENTICATOR_SETUP" for="authenticatorQrUrl">{{ t('login.authenticatorSetup') }}</label>
						<img v-show="mode===LoginDialogMode.AUTHENTICATOR_SETUP" :src="authenticatorQrUrl" id="authenticatorQrUrl" alt="Authenticator QR code" class="mb-3" />
						<label for="authenticatorToken">{{ t('login.authenticatorCode') }}</label>
						<InputOtp v-model="auth.authenticatorToken" id="authenticatorToken" :length="6" size="large" @keyup.enter="login" :disabled="isWorking"
							:pt="{ input: { 'autocomplete': 'section-login one-time-code' } }" />
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 flex flex-col justify-center" v-show="mode===LoginDialogMode.PASSWORD_RESET">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.resetPassword') }}</div>
						<div class="flex justify-center items-center space-x-2">
							<div class="w-72 mb-6 mt-3 text-pretty">{{ t('login.resetPasswordHelp') }}</div>
						</div>
						<FloatLabel variant="on">
							<InputText v-model="resetData.username" type="email" fluid id="resetemail" @keyup.enter="reset" :disabled="isWorking" :minlength="3" />
							<label for="resetemail">{{ t('login.email') }}</label>
						</FloatLabel>
						<Select v-model="resetData.method" class="text-left mt-3" fluid :options="resetMethods" option-label="text" option-value="value" append-to="self" />
						<div class="mt-5 flex justify-center space-x-2">
							<Button @click="gotoUsernamePassword()" severity="secondary" fluid :disabled="isWorking">{{ t('common.cancel') }}</Button>
							<Button @click="reset()" severity="primary" fluid :disabled="isWorking || !resetData.username">{{ t('login.next') }}</Button>
						</div>
					</div>
				</Transition>

				<Transition name="section">
					<div v-show="mode===LoginDialogMode.PASSWORD_RESET_EMAIL_COMPLETED">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.resetPassword') }}</div>
						<div class="mb-4 mt-2">{{ t('login.resetPasswordHelp2') }}</div>
						<Button @click="gotoUsernamePassword(true)" fluid :disabled="isWorking">{{ t('login.ok') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div v-show="mode===LoginDialogMode.PASSWORD_RESET_SMS_COMPLETED">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.resetPassword') }}</div>
						<div class="mb-4 mt-2">{{ t('login.resetPasswordHelp3') }}</div>
						<Button @click="gotoUsernamePassword(true)" fluid :disabled="isWorking">{{ t('login.ok') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 flex flex-col justify-center" v-show="mode===LoginDialogMode.PASSKEY">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.passkeyOfferHeader') }}</div>
						<div class="w-72 mb-6 mt-3 text-pretty mx-auto">{{ t('login.passkeyOfferText') }}</div>
						<Button @click="acceptPasskeyOffer()" fluid :disabled="isWorking">{{ t('login.passkeyOfferCreate') }}</Button>
						<Button @click="declinePasskeyOffer()" variant="text" fluid class="mt-2" :disabled="isWorking">{{ t('login.passkeyOfferLater') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 flex flex-col justify-center" v-show="mode===LoginDialogMode.PASSKEY_ENROLL">
						<div class="text-4xl" style="color:var(--p-text-muted-color)">{{ t('login.passkeyCodeButton') }}</div>
						<div class="w-72 mb-6 mt-3 text-pretty mx-auto">{{ t('login.passkeyCodeHelp') }}</div>
						<FloatLabel variant="on">
							<InputText v-model="passkeyCode" id="passkeycode" autocomplete="off" @keyup.enter="enrollWithCode" :disabled="isWorking" fluid />
							<label for="passkeycode">{{ t('login.passkeyCodeLabel') }}</label>
						</FloatLabel>
						<Button class="mt-3" @click="enrollWithCode()" fluid :disabled="isWorking || !enrollCodeValid">{{ t('login.passkeyOfferCreate') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 flex flex-col justify-center" v-show="electron.webauthnAccountRequest">
						<div class="mb-3 text-pretty">{{ t('login.passkeyChooseAccount') }}</div>
						<Button v-for="account in electron.webauthnAccountRequest?.accounts ?? []" :key="account.credentialId" class="mb-2" severity="secondary" fluid
							@click="electron.selectWebauthnAccount(account.credentialId)">{{ account.displayName ?? account.name ?? account.credentialId }}</Button>
						<Button variant="text" fluid @click="electron.selectWebauthnAccount(null)">{{ t('common.cancel') }}</Button>
					</div>
				</Transition>

				<Transition name="section">
					<div class="pl-8 pr-8 pt-4 mt-5 flex justify-center space-x-2" v-show="!mode.startsWith('PASSWORD_RESET') && mode!==LoginDialogMode.PASSKEY">
						<Button @click="gotoUsernamePassword()" severity="secondary" fluid v-if="mode!==LoginDialogMode.USERNAME_PASSWORD" >{{ t('common.cancel') }}</Button>
						<Button @click="loginWithPasskey()" severity="secondary" fluid :disabled="isWorking" v-if="passkeyAvailable && mode===LoginDialogMode.USERNAME_PASSWORD">{{ t('login.passkeyButton') }}</Button>
						<Button @click="login()" fluid :disabled="loginButtonDisabled" v-if="mode!==LoginDialogMode.PASSKEY_ENROLL">{{ t('login.loginButton') }}</Button>
					</div>
				</Transition>
			</form>

		</div>
	</div>
</template>

<script setup lang="ts">
import axios, { AxiosError } from 'axios'
import { useSettingsStore } from '@/stores/Settings'
import { useI18n } from 'vue-i18n'
import { type LoginResponse, type LoginRequest, type ValidateTokenExtendedResponse, type ResetRequest, type ResetResponse, LoginResponseErrorCodes, ResetResponseErrorCodes } from '@/TcxApi/Authentication'
import { ref, computed, onMounted } from 'vue'
import { LoginDialogMode } from '@/definitions/Common'
import { CustomerFeature } from '@/TcxApi/Customer'
import { useElectronStore } from '@/stores/Electron'
import { cancelPendingPasskey, ceremonyDuration, clearPasskeyLoginFailure, isMissingLocalPasskeyError, passkeyAutofillSupported, passkeyEnroll, passkeyList, passkeyLogin, passkeyLoginAutofill, passkeyLoginAvailable, passkeyRegister,
	passkeyStoreIsPrivate, passkeysSupported, platformAuthenticatorAvailable, setDeclinedPasskeyOffer, setPasskeyCreatedHere, setPasskeyLoginFailed, shouldOfferPasskey } from '@/utilities/Passkeys'
import type { ErrorResponse } from '@/TcxApi/ErrorResponse'
import { WebauthnErrorCodes } from '@/TcxApi/Webauthn'
import Message from 'primevue/message'
import GlyphIcon from './GlyphIcon.vue'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

/** Length of the enrollment code the API issues - a shorter or longer paste cannot be a code */
const PASSKEY_CODE_LENGTH = 43

const { t } = useI18n()
const ss = useSettingsStore()
const electron = useElectronStore()
const auth = ref<LoginRequest>({ username: '', password: '', smsKey: '', authenticatorToken: '', cookie: false, persist: false })
const errorMessage = ref<string | null>(null)
/** Confirmation of a completed step, such as a created passkey - kept apart from errorMessage so it renders as a success */
const successMessage = ref<string | null>(null)
const isWorking = ref(false)
const mode = ref<LoginDialogMode>(LoginDialogMode.USERNAME_PASSWORD)
const authenticatorQrUrl = ref<string | undefined>(undefined)
const resetData = ref<ResetRequest>({ username: '', method: 'EMAIL' })
const resetMethods = ref<{ value: string, text: string }[]>([
	{ value: 'EMAIL', text: t('login.sendToEmail') },
	{ value: 'SMS', text: t('login.sendToSms') }
])
const passkeySupported = ref(passkeysSupported())
/** Whether the passkey buttons are shown. Final for a browser at once; inside the wrapper it waits for the platform authenticator probe in onMounted, so the buttons never flash */
const passkeyAvailable = ref(!ss.runtime.isEmbedded && passkeySupported.value)
/** True once the wrapper has been found without a platform authenticator - shows the hint instead of buttons that would hang */
const passkeyUnavailable = ref(false)
/** Whether the enrollment code button is shown. A code is only ever issued by a manager to a passkey only account, so the button waits until the API has said the account is one - or the app was opened from an enrollment link */
const passkeyCodeOffered = ref(false)
const passkeyCode = ref('')
const pendingLogin = ref<LoginResponse | null>(null)
const enrollCodeValid = computed(() => { return passkeyCode.value.trim().length === PASSKEY_CODE_LENGTH })

async function login() {
	if (!auth.value.username || !auth.value.password) { return }
	cancelPendingPasskey() // A password login is not a ceremony, so the passkey sheet would otherwise hang over the 2-factor step
	isWorking.value = true
	errorMessage.value = null
	successMessage.value = null

	try {
		const res = await axios.post<LoginResponse>(`${ss.apiUrl}/auth/login`, auth.value, { timeout: 10000 })

		if (!res.data.customerFeatures.includes(CustomerFeature.PBX) || !res.data.communicatorAccess) {
			errorMessage.value = t('login.errorNoCommunicatorAccess')
			auth.value.smsKey = ''
			auth.value.authenticatorToken = ''
			mode.value = LoginDialogMode.USERNAME_PASSWORD
			setTimeout(() => { document.getElementById('password')?.focus() }, 10)
			return
		}

		authenticatorQrUrl.value = undefined
		mode.value = LoginDialogMode.USERNAME_PASSWORD
		ss.runtime.rememberMe = auth.value.persist
		ss.runtime.emailAddress = auth.value.username

		// Offer a passkey when this device can hold one and, as far as can be known from here, holds none the account can use -
		// the login is held until the user answers the offer. The rules live in shouldOfferPasskey(); the API's list is only
		// consulted where the credential store is shared, since it cannot tell what the macOS wrapper's private store holds
		if (passkeySupported.value && await platformAuthenticatorAvailable()) {
			const privateStore = passkeyStoreIsPrivate(ss.runtime.isEmbedded)
			const credentials = privateStore ? [] : await passkeyList(ss.apiUrl, res.data.token as string).catch(() => [])

			if (shouldOfferPasskey({ employeeId: res.data.employee, privateStore, credentials, hostname: location.hostname })) {
				clearPasskeyLoginFailure() // The failed attempt that may have forced this offer has done its job
				pendingLogin.value = res.data
				mode.value = LoginDialogMode.PASSKEY
				isWorking.value = false
				return
			}
		}

		isWorking.value = false
		ss.initAfterLoginOrValidate(res.data as ValidateTokenExtendedResponse)
	}
	catch (res) {
		isWorking.value = false

		if (mode.value === LoginDialogMode.SMS_ENTRY || mode.value === LoginDialogMode.AUTHENTICATOR_ENTRY) {
			auth.value.smsKey = ''
			auth.value.authenticatorToken = ''
			mode.value = LoginDialogMode.USERNAME_PASSWORD
			setTimeout(() => { document.getElementById('password')?.focus() }, 500)
		}

		if (!(res as AxiosError).response?.data) {
			errorMessage.value = t('login.errorNoConnection')
			return
		}

		const err = ((res as AxiosError).response?.data as ErrorResponse)?.error

		if (err.status === 500) {
			errorMessage.value = t('login.errorServer')
		} else {
			switch (err.code) {
				case LoginResponseErrorCodes.credentials_invalid:
					errorMessage.value = t('login.errorCredentials')
					document.getElementById('password')?.focus()
					break
				case LoginResponseErrorCodes.account_disabled:
					errorMessage.value = t('login.errorDisabled')
					break
				case LoginResponseErrorCodes.ipaddress_invalid:
					errorMessage.value = t('login.errorWrongIp')
					break
				case LoginResponseErrorCodes.sms_error:
					errorMessage.value = t('login.errorServer')
					break
				case LoginResponseErrorCodes.sms_authenticate:
					mode.value = LoginDialogMode.SMS_ENTRY
					setTimeout(() => {
						(document.querySelector('#smsKey>INPUT') as HTMLInputElement)?.focus()
					}, 500)
					break
				case LoginResponseErrorCodes.sms_key_invalid:
					errorMessage.value = t('login.errorSmsCodeInvalid')
					break
				case LoginResponseErrorCodes.authenticator_authenticate:
					mode.value = LoginDialogMode.AUTHENTICATOR_ENTRY
					setTimeout(() => {
						(document.querySelector('#authenticatorToken>INPUT') as HTMLInputElement)?.focus()
					}, 500)
					break
				case LoginResponseErrorCodes.authenticator_key_invalid:
					errorMessage.value = t('login.authenticatorCodeInvalid')
					break
				case LoginResponseErrorCodes.webauthn_only:
					// The account may only sign in with a passkey. The ceremony is NOT started here - a prompt nobody
					// asked for is usernameless, so on a shared device the browser can offer somebody else's passkey
					errorMessage.value = t('login.passkeyOnly')
					auth.value.password = ''
					passkeyCodeOffered.value = true // The enrollment code a manager issues is this account's way in here
					break
				case LoginResponseErrorCodes.authenticator_setup:
					mode.value = LoginDialogMode.AUTHENTICATOR_SETUP
					setTimeout(() => { document.getElementById('authenticatorToken')?.focus() }, 500)
					authenticatorQrUrl.value = `https://api.telecomx.dk/tools/qr?text=${encodeURIComponent((err as unknown as { totp: string }).totp)}`
					break
			}
		}
	}
}

function showReset() {
	mode.value = LoginDialogMode.PASSWORD_RESET
	errorMessage.value = null
	successMessage.value = null
	setTimeout(() => { document.getElementById('resetemail')?.focus() }, 500)
}

async function reset() {
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetData.value.username)) { return } // Ensure valid email address
	cancelPendingPasskey() // Leaving the password form must take the browser's passkey sheet with it
	isWorking.value = true
	errorMessage.value = null

	try {
		const res = await axios.post<ResetResponse>(`${ss.apiUrl}/auth/reset`, resetData.value, { timeout: 10000 })

		isWorking.value = false
		if (res.data.method === 'EMAIL') {
			mode.value = LoginDialogMode.PASSWORD_RESET_EMAIL_COMPLETED
		} else {
			mode.value = LoginDialogMode.PASSWORD_RESET_SMS_COMPLETED
		}
	}
	catch (res) {
		isWorking.value = false

		if (!(res as AxiosError).response?.data) {
			errorMessage.value = t('login.errorNoConnection')
			return
		}

		const err = ((res as AxiosError).response?.data as ErrorResponse).error

		if (err.status === 500) {
			errorMessage.value = t('login.errorServer')
		} else {
			switch (err.code) {
				case ResetResponseErrorCodes.account:
					errorMessage.value = t('login.errorDisabled')
					break
				case ResetResponseErrorCodes.username:
					errorMessage.value = t('login.errorResetNotAvailable')
					break
				case ResetResponseErrorCodes.time:
					errorMessage.value = t('login.errorResetAlreadyInProgress')
					break
			}
		}
	}
}

function gotoUsernamePassword(fill = false) {
	mode.value = LoginDialogMode.USERNAME_PASSWORD
	if (fill) {
		auth.value.username = resetData.value.username
	} else {
		resetData.value.username = ''
	}
	auth.value.password = ''
	auth.value.smsKey = ''
	auth.value.authenticatorToken = ''
	passkeyCode.value = ''

	resetData.value.method = 'EMAIL'
	errorMessage.value = null

	setTimeout(() => {
		if (auth.value.username) {
			document.getElementById('password')?.focus()
		} else {
			document.getElementById('email')?.focus()
		}
	}, 500)
}

/**
 * Completes a passkey login exactly as a password login is completed - same access check, same store call.
 * @param data Payload the passkey authentication endpoint returned
 * @param persist True when a persistent token was asked for, so the token is stored the same way
 */
function completePasskeyLogin(data: LoginResponse, persist: boolean) {
	if (!data.customerFeatures.includes(CustomerFeature.PBX) || !data.communicatorAccess) {
		errorMessage.value = t('login.errorNoCommunicatorAccess')
		mode.value = LoginDialogMode.USERNAME_PASSWORD
		return
	}

	ss.runtime.rememberMe = persist
	ss.initAfterLoginOrValidate(data as ValidateTokenExtendedResponse)
}

/**
 * Signs in with a passkey and hands the result to the same code path a password login uses. Only ever started
 * by the user pressing the passkey button.
 */
async function loginWithPasskey() {
	isWorking.value = true
	errorMessage.value = null
	successMessage.value = null

	try {
		const data = await passkeyLogin(ss.apiUrl, auth.value.persist === true)

		clearPasskeyLoginFailure()
		setPasskeyCreatedHere(data.employee) // A login proves a passkey lives in this application's store - only consulted where that store is private
		completePasskeyLogin(data, auth.value.persist === true)
	}
	catch (err) {
		mode.value = LoginDialogMode.USERNAME_PASSWORD

		if (isMissingLocalPasskeyError(err, ceremonyDuration(err), ss.runtime.isEmbedded)) {
			// Chromium answered before any prompt could appear - the wrapper's own store holds nothing for this site, which
			// is what happens when the passkey was made in a browser or on a phone. The next password login offers to create one
			setPasskeyLoginFailed()
			errorMessage.value = t('login.passkeyMissing')
		} else if ((err as Error)?.name === 'NotAllowedError') {
			errorMessage.value = t('login.passkeyCancelled')
		} else {
			// The credential was fine but the API refused the session - report why, as the password path does
			const code = ((err as AxiosError).response?.data as ErrorResponse)?.error?.code

			if (code === LoginResponseErrorCodes.ipaddress_invalid) {
				errorMessage.value = t('login.errorWrongIp')
			} else if (code === LoginResponseErrorCodes.account_disabled) {
				errorMessage.value = t('login.errorDisabled')
			} else {
				errorMessage.value = t('login.passkeyFailed')
			}
		}
	}
	finally {
		isWorking.value = false
		electron.selectWebauthnAccount(null) // Clears a stale chooser panel when the ceremony ended by timeout rather than a button press
	}
}

/** Shows the panel where a passkey is created from the code in an enrollment e-mail or SMS. */
function showPasskeyEnroll() {
	mode.value = LoginDialogMode.PASSKEY_ENROLL
	errorMessage.value = null
	successMessage.value = null
	setTimeout(() => { document.getElementById('passkeycode')?.focus() }, 500)
}

/**
 * Registers a passkey from the code in an enrollment e-mail or SMS. This is the only way a passkey only
 * employee can recover on the macOS desktop build, where the credential store is private to the app.
 * A failure never consumes the code (only the enroll call itself does, not enroll/options), so it stays
 * in the field and is reported by cause the same way loginWithPasskey() reports its failures.
 */
async function enrollWithCode() {
	if (!enrollCodeValid.value) { return }
	cancelPendingPasskey()
	isWorking.value = true
	errorMessage.value = null

	try {
		await passkeyEnroll(ss.apiUrl, passkeyCode.value.trim())
		passkeyCode.value = ''
		mode.value = LoginDialogMode.USERNAME_PASSWORD
		successMessage.value = t('login.passkeyCreated')
	}
	catch (err) {
		if ((err as Error)?.name === 'NotAllowedError') {
			errorMessage.value = t('login.passkeyCancelled')
		} else {
			const code = ((err as AxiosError).response?.data as ErrorResponse)?.error?.code

			errorMessage.value = code === WebauthnErrorCodes.ENROLL_TOKEN_INVALID ? t('login.passkeyCodeInvalid') : t('login.passkeyFailed')
		}
	}
	finally { isWorking.value = false }
}

/**
 * Creates a passkey from the post login offer and completes the login. The user started this ceremony by pressing
 * Create, so a failure is reported and the offer stays up with both actions - retry, or decline and get logged in.
 */
async function acceptPasskeyOffer() {
	isWorking.value = true
	errorMessage.value = null

	try {
		await passkeyRegister(ss.apiUrl, pendingLogin.value?.token as string)
		setPasskeyCreatedHere(pendingLogin.value?.employee as string)
		ss.initAfterLoginOrValidate(pendingLogin.value as ValidateTokenExtendedResponse)
	}
	catch (err) {
		errorMessage.value = (err as Error)?.name === 'NotAllowedError' ? t('login.passkeyCancelled') : t('login.passkeyFailed')
	}
	finally { isWorking.value = false }
}

/** Declines the offer, remembers that for 90 days so it is not asked again, and completes the login. */
function declinePasskeyOffer() {
	if (!pendingLogin.value) { return }

	setDeclinedPasskeyOffer(pendingLogin.value.employee)
	ss.initAfterLoginOrValidate(pendingLogin.value as ValidateTokenExtendedResponse)
}

const loginButtonDisabled = computed(() => {
	if (isWorking.value) { return true }
	if (!auth.value.username || !auth.value.password) { return true }
	if (mode.value === LoginDialogMode.SMS_ENTRY && (!auth.value.smsKey || (auth.value.smsKey && auth.value.smsKey.length < 4))) { return true }
	if (mode.value === LoginDialogMode.AUTHENTICATOR_ENTRY && (!auth.value.authenticatorToken || (auth.value.authenticatorToken && auth.value.authenticatorToken.length < 6))) { return true }
	return false
})

onMounted(async () => {
	auth.value.username = ss.runtime.emailAddress ?? ''
	passkeyAvailable.value = await passkeyLoginAvailable(ss.runtime.isEmbedded)
	passkeyUnavailable.value = ss.runtime.isEmbedded && !passkeyAvailable.value
	setTimeout(() => {
		if (auth.value.username) {
			document.getElementById('password')?.focus()
		} else {
			document.getElementById('email')?.focus()
		}
	}, 10)

	// An enrollment link pre-fills the code and opens straight into PASSKEY_ENROLL - the user still presses
	// Create, so the ceremony itself is never started without a gesture
	if (ss.runtime.passkeyToken) {
		passkeyCode.value = ss.runtime.passkeyToken
		passkeyCodeOffered.value = true
		mode.value = LoginDialogMode.PASSKEY_ENROLL
		ss.runtime.passkeyToken = undefined
	}

	// A conditional request puts any passkey for this site in the username field's autofill dropdown. Nobody
	// asked for it, so it is started once and every rejection is swallowed without a word to the user.
	// Skipped inside the Electron wrapper - there is no autofill UI to fill, and what its request delegate
	// does with a conditional request against the platform authenticator is undocumented; the button remains
	if (!ss.runtime.isEmbedded && passkeySupported.value && await passkeyAutofillSupported()) {
		try { completePasskeyLogin(await passkeyLoginAutofill(ss.apiUrl), false) }
		catch (_) { /* Aborted or unsupported - the button and password paths remain */ }
	}
})
</script>

<style scoped>
	.box {
		width: 500px;
		padding: 30px;
		background-color: var(--p-surface-300);
		text-align: center;
		border-radius: 10px;
		box-shadow: var(--modal-shadow);
	}
	@media(prefers-color-scheme: dark) {
		.box {
			background-color: var(--p-surface-900);
		}
	}
	.logo {
		width: 300px;
		padding-bottom: 25px;
	}

	.errormessage-enter-active {
		transition:
			max-height 0.4s ease-out,
			opacity 0.4s ease-out 0.4s;
		overflow: hidden;
	}

	.errormessage-leave-active {
		transition: max-height 0.1s ease-out, opacity 0.1s ease-out;
		overflow: hidden;
	}

	.errormessage-enter-from,
	.errormessage-leave-to {
		max-height: 0;
		opacity: 0;
	}

	.errormessage-enter-to,
	.errormessage-leave-from {
		max-height: 200px;
		/* Set a large enough value */
		opacity: 1;
	}

	.section-enter-active {
		transition: max-height 0.2s ease-out 0.2s;
		overflow: hidden;
	}

	.section-leave-active {
		transition: max-height 0.2s ease-out;
		overflow: hidden;
	}

	.section-enter-from,
	.section-leave-to {
		max-height: 0;
	}

	.section-enter-to,
	.section-leave-from {
		max-height: 200px;
	}

</style>
